import {
  BreadcrumbSlashIcon,
  ExcelFileIcon,
  ImageFile,
  PDFIcon,
} from '@commenda/icons';
import { FileIcon, FilledFolderIcon } from '@commenda/icons/folder-icons';
import { Button, DataTable, Dropdown, Icons } from '@commenda/kakashi';
import { INewFolder } from '@commenda/types/src/drive-view/types';
import { FileBasic } from '@commenda/types/src/file/types';
import {
  classNames,
  documentName,
  DocumentViewUIHelper,
  downloadFileFromLink,
  getFileComponents,
  NonTimezoneSensitiveDate,
} from '@commenda/utils';
import { IMAGE_EXTENSIONS } from '@commenda/utils/files/constants';
import { getSignedUrlByFileId } from '@commenda/utils/files/uploadFile';
import React, { useEffect, useMemo, useState } from 'react';
import {
  getColumnItems,
  resolveFolderPath,
} from './document-folder-view.utils';

// Helper function to find path to target folder using DFS
function findPathToFolder(
  currentFolder: INewFolder,
  targetId: string,
  currentPath: { id: string | number; name: string }[] = []
): { id: string | number; name: string }[] | null {
  // Add current folder to path
  const newPath = [
    ...currentPath,
    { id: currentFolder.id, name: currentFolder.name },
  ];

  // If this is the target folder, return the path
  if (currentFolder.id.toString() === targetId) {
    return newPath;
  }

  // Search through subfolders
  if (currentFolder.folders) {
    for (const folder of currentFolder.folders) {
      const result = findPathToFolder(folder, targetId, newPath);
      if (result) {
        return result;
      }
    }
  }

  return null;
}

export function getFileIconByExtension(
  fileName: string,
  _className?: string
): React.ReactNode {
  const { extension } = getFileComponents(fileName);

  if (IMAGE_EXTENSIONS.includes(extension))
    return <ImageFile color="#007F8C" className={_className} />;

  if (extension === 'xlsx')
    return <ExcelFileIcon color="#0F7638" className={_className} />;

  if (extension === 'pdf')
    return <PDFIcon color="#0F7638" className={_className} />;

  return <FileIcon color="#1177E3" className={_className} />;
}

function getColumns({
  onFileDownload,
  onFolderClick,
  token,
  onPreviewFile,
  onDeleteFile,
}: {
  onFolderClick: (folderId: string | number, folderName: string) => unknown;
  onFileDownload: (fileId: number, fileName: string) => Promise<string>;
  token: string;
  onPreviewFile: (file: FileBasic) => void;
  onDeleteFile?: (fileId: number, fileName: string) => void;
}) {
  return [
    {
      accessorKey: 'name',
      header: 'Name',
      cell: ({ row }) => {
        if (row.original.type === 'Folder') {
          return (
            <div className="flex gap-3 items-center cursor-pointer h-9">
              <FilledFolderIcon className="size-5" />
              <div className="flex items-center gap-1">
                <span className="txt-small-medium truncate max-w-[320px]">
                  {row.original.name}
                </span>
                {row?.original?.noOfFiles !== null &&
                  row?.original?.noOfFiles !== undefined && (
                    <span className="text-xs text-text-quinary font-normal">
                      ({row?.original?.noOfFiles}{' '}
                      {row?.original?.noOfFiles === 1 ? 'file' : 'files'})
                    </span>
                  )}
              </div>
            </div>
          );
        }

        const extensionIcon = getFileIconByExtension(
          row.original.name,
          'size-5'
        );

        return (
          <div
            className="flex gap-3 items-center "
            onDoubleClick={async () => {
              const signedUrl = await onFileDownload(
                +row.original.id,
                row.original.name
              );
              if (!signedUrl) return;
              downloadFileFromLink(signedUrl, row.original.name);
            }}
          >
            {extensionIcon}
            <span className="txt-small-medium truncate max-w-[320px]">
              {row.original.name}
            </span>
          </div>
        );
      },
      enableSorting: true,
      sortingFn: (rowA, rowB) => {
        return rowA.original.name > rowB.original.name ? 1 : -1;
      },
    },
    {
      accessorKey: 'Type',
      header: 'Type',
      cell: ({ row }) => {
        if (row.original.type === 'Folder') return null;

        return (
          <div
            className="flex gap-3 items-center"
            onDoubleClick={async () => {
              const signedUrl = await onFileDownload(
                +row.original.id,
                row.original.name
              );
              if (!signedUrl) return;
              downloadFileFromLink(signedUrl, row.original.name);
            }}
          >
            <span className="txt-small-medium">
              {documentName(row.original.documentType) ||
                row.original.explicitDocumentType}
            </span>
          </div>
        );
      },
      enableSorting: true,
      sortingFn: (rowA, rowB) => {
        if (rowA.original.type === 'Folder' && rowB.original.type === 'Folder')
          return -1;
        return rowA.original.documentType > rowB.original.documentType ? -1 : 1;
      },
    },
    {
      accessorKey: 'lastModifiedDate',
      accessorFn: null,
      header: 'Last modified',
      cell: ({ row }) => {
        return (
          <span className="txt-small-medium">
            {row?.original?.type === 'File'
              ? row.original.lastModifiedDate
                ? NonTimezoneSensitiveDate.fromISO8601String(
                  row.original.lastModifiedDate
                ).format('MMM DD, YYYY')
                : 'N/A'
              : null}
          </span>
        );
      },
    },
    {
      accessorKey: 'Actions',
      header: null,
      cell: ({ row }) => {
        if (row.original.type === 'Folder') return null;

        const shouldShowOpenInNewTab =
          DocumentViewUIHelper.shouldShowOpenInNewTab(row.original.name);

        const shouldShowPreview = DocumentViewUIHelper.shouldShowPreview(
          row.original.name
        );

        return (
          <Dropdown
            menu={{
              groups: [
                {
                  items: [
                    ...(onPreviewFile && shouldShowPreview
                      ? [
                        {
                          label: 'Preview',
                          icon: Icons.Eye,
                          onClick: () => {
                            if (onPreviewFile) onPreviewFile(row.original);
                          },
                        },
                      ]
                      : []),
                    {
                      label: 'Download',
                      icon: Icons.Download01,
                      onClick: async () => {
                        const url = await getSignedUrlByFileId(
                          row.original.id,
                          token
                        );

                        if (url) downloadFileFromLink(url, row.original.name);
                      },
                    },
                    ...(shouldShowOpenInNewTab
                      ? [
                        {
                          label: 'Open in new tab',
                          icon: Icons.ArrowUpRight,
                          onClick: async () => {
                            const url = await getSignedUrlByFileId(
                              row.original.id,
                              token
                            );

                            if (url) window.open(url, '_blank');
                          },
                        },
                      ]
                      : []),
                  ],
                },
                ...(onDeleteFile
                  ? [
                    {
                      items: [
                        {
                          label: 'Delete',
                          icon: Icons.Trash01,
                          iconClassName: 'text-utility-error-500',
                          labelClassName: 'text-utility-error-500',
                          onClick: () => {
                            onDeleteFile(
                              +row.original.id,
                              row.original.name
                            );
                          },
                        },
                      ],
                    },
                  ]
                  : []),
              ],
            }}
            align="end"
          >
            <Button variant="tertiary" size="sm">
              <Icons.DotsVertical className="h-5 w-5" />
            </Button>
          </Dropdown>
        );
      },
    },
  ];
}

const DocumentsFolderView = ({
  rootFolder,
  defaultOpenFolderId,
  defaultOpenFolderName,
  downloadFile,
  className,
  onPreviewFile,
  onFolderChange,
  onDeleteFile,
}: {
  rootFolder: INewFolder;

  // In some cases we want to directly open a folder
  // Right now it only supports opening folder on L1.
  defaultOpenFolderId?: string;
  defaultOpenFolderName?: string;

  // We pass a download function that takes in a fileId and returns the signed S3 URL because
  // different callers can be using different authorization methods. Example:
  // In enterprise app the download function might not need token but in accountant app token is required
  downloadFile: (fileId: number, fileName: string) => Promise<string>;

  className?: string;

  onPreviewFile?: (file: FileBasic) => void;

  onFolderChange?: (folderId: string, folderName: string) => void;

  onDeleteFile?: (fileId: number, fileName: string) => void;
}) => {
  const [folderLevels, setFolderLevels] = useState<
    { id: string | number; name: string }[]
  >([]);

  const [tree, setTree] = useState<INewFolder>(rootFolder);

  useEffect(() => {
    setTree(rootFolder);
  }, [rootFolder]);

  const columns = useMemo(
    () =>
      getColumns({
        onFolderClick: (folderId, folderName) => {
          setFolderLevels((prev) => {
            const newLevels = [...prev, { id: folderId, name: folderName }];
            onFolderChange?.(folderId.toString(), folderName);
            return newLevels;
          });
        },
        token: undefined,
        onFileDownload: downloadFile,
        onPreviewFile,
        onDeleteFile,
      }),
    [tree, rootFolder, onPreviewFile, onFolderChange, onDeleteFile]
  );

  useEffect(() => {
    // Initialize folder levels with root folder
    const initialLevels = [
      {
        id: rootFolder.id,
        name: rootFolder.name,
      },
    ];

    // If default open folder is provided, find the complete path to it
    if (defaultOpenFolderId && defaultOpenFolderName) {
      const pathToFolder = findPathToFolder(rootFolder, defaultOpenFolderId);
      if (pathToFolder) {
        // Skip the first element since it's the root folder which we already added
        setFolderLevels(pathToFolder);
        onFolderChange?.(defaultOpenFolderId, defaultOpenFolderName);
        return;
      }
    }

    setFolderLevels(initialLevels);
  }, [rootFolder, defaultOpenFolderId, defaultOpenFolderName, onFolderChange]);

  const resolvedPath = resolveFolderPath(
    [...folderLevels.map((level) => level.id)],
    tree
  );

  const displayItems = getColumnItems(resolvedPath);

  return (
    <div className="flex flex-col" data-ai-section="Files and folders">
      <div className="w-full flex flex-col">
        <div className="py-[14px] px-6 flex items-center gap-1 h-[62px] translate-y-2 rounded-t-xl">
          {folderLevels.map((level, index) => {
            return (
              <div
                key={level?.id}
                className="py-1 px-2 cursor-pointer flex items-center gap-2"
                onClick={() => {
                  setFolderLevels((prev) => {
                    const copy = [...prev];
                    copy.splice(index + 1);
                    onFolderChange?.(
                      copy[copy.length - 1].id.toString(),
                      copy[copy.length - 1].name
                    );
                    return copy;
                  });
                }}
              >
                <span
                  className={classNames(
                    'txt-small-medium text-text-quaternary shrink-0 -translate-x-2 -translate-y-[3px]',
                    index === folderLevels.length - 1
                      ? 'text-text-quaternary'
                      : 'text-text-quaternary-hover'
                  )}
                >
                  {level.name}
                </span>
                {index !== folderLevels.length - 1 && (
                  <BreadcrumbSlashIcon className="text-fg-quinary -translate-y-[3px] shrink-0" />
                )}
              </div>
            );
          })}
        </div>
        <DataTable
          rowClassName={() => 'hover:cursor-pointer hover:bg-neutral-6'}
          rowDataAttributes={(row) => ({
            'data-ai-item': row.type === 'Folder' ? 'Folder' : 'Document',
            ...(row.id ? { 'data-ai-id': String(row.id) } : {}),
          })}
          data={displayItems}
          columnGroups={[{ id: 'table', columns }]}
          tableWrapperClassName={className}
          emptyStateConfig={{
            title: 'No files found',
            description: 'There are no files in this folder',
          }}
          onRowClick={(row) => {
            if (row.type === 'Folder') {
              setFolderLevels((prev) => {
                const newLevels = [...prev, { id: row.id, name: row.name }];
                onFolderChange?.(row.id.toString(), row.name);
                return newLevels;
              });
            } else if (row.type === 'File') {
              const shouldShowPreview = DocumentViewUIHelper.shouldShowPreview(
                row.name
              );
              if (onPreviewFile && shouldShowPreview) {
                const fileBasic: FileBasic = {
                  fileName: row.name,
                  fileUrl: '', // This will be populated by the preview handler
                  documentType: row.documentType,
                  id:
                    typeof row.id === 'number'
                      ? row.id
                      : parseInt(row.id.toString()),
                };
                onPreviewFile(fileBasic);
              }
            }
          }}
        />
      </div>
    </div>
  );
};

export default DocumentsFolderView;
